/*
  Single source of truth for the file extensions Pixelpa's Open dialog and
  drag-drop pipeline accept. Matches Photoshop / Photopea's commonly-opened set.

  Formats split into two buckets:

    SUPPORTED_EXTENSIONS  — real decoders exist; openFile() will parse them.
    PLACEHOLDER_EXTENSIONS — file picker advertises them so users don't get
                              stuck at the OS dialog, but openFile() will
                              show a graceful "parser not yet implemented"
                              tip rather than crashing.

  Pure module — no DOM, no side-effects. Exported helpers are unit-testable.
  Closes #119.
*/

/** Extensions we can fully decode today. */
export const SUPPORTED_EXTENSIONS = [
  ".psd", ".psb",
  ".png", ".jpg", ".jpeg", ".webp", ".avif", ".gif",
  ".tiff", ".tif", ".bmp", ".svg", ".ico",
  ".pxa",
  ".pdf",
] as const;

/** Extensions advertised but not yet parsed; openFile() reports gracefully. */
export const PLACEHOLDER_EXTENSIONS = [
  ".ai", ".eps",
  ".heic", ".heif",
  ".raw", ".dng", ".cr2", ".nef", ".arw", ".raf", ".orf",
  ".xd", ".sketch", ".fig", ".xcf",
  ".indd", ".idml",
  ".tga", ".dds",
  ".exr", ".hdr",
  ".jp2", ".jpx",
] as const;

export type SupportedExtension = (typeof SUPPORTED_EXTENSIONS)[number];
export type PlaceholderExtension = (typeof PLACEHOLDER_EXTENSIONS)[number];
export type AcceptedExtension = SupportedExtension | PlaceholderExtension;

/**
 * Value for the `<input type=file>` `accept` attribute. Browsers ignore
 * unknown ones; we list them anyway so future support is one-line.
 */
export const FILE_INPUT_ACCEPT: string = [
  ...SUPPORTED_EXTENSIONS,
  ...PLACEHOLDER_EXTENSIONS,
].join(",");

/** Lowercase extension including the dot, or "" if the name has none. */
export function extOf(name: string): string {
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot < 0) return "";
  return lower.slice(dot);
}

export function isSupportedExtension(ext: string): boolean {
  return (SUPPORTED_EXTENSIONS as readonly string[]).includes(ext.toLowerCase());
}

export function isPlaceholderExtension(ext: string): boolean {
  return (PLACEHOLDER_EXTENSIONS as readonly string[]).includes(ext.toLowerCase());
}

/** Display-friendly format name for the "parser not implemented" message. */
export function formatLabel(ext: string): string {
  const e = ext.toLowerCase().replace(/^\./, "");
  const map: Record<string, string> = {
    ai: "Adobe Illustrator", pdf: "PDF", eps: "EPS",
    heic: "HEIC", heif: "HEIF",
    raw: "RAW", dng: "DNG", cr2: "Canon RAW (CR2)", nef: "Nikon RAW (NEF)",
    arw: "Sony RAW (ARW)", raf: "Fujifilm RAW (RAF)", orf: "Olympus RAW (ORF)",
    xd: "Adobe XD", sketch: "Sketch", fig: "Figma", xcf: "GIMP (XCF)",
    indd: "InDesign", idml: "InDesign Markup",
    tga: "Targa", dds: "DirectDraw Surface",
    exr: "OpenEXR", hdr: "Radiance HDR",
    jp2: "JPEG 2000", jpx: "JPEG 2000 (JPX)",
  };
  return map[e] ?? e.toUpperCase();
}

export function unimplementedMessage(name: string): string {
  const ext = extOf(name);
  const label = ext ? formatLabel(ext) : "this format";
  return `Cannot open ${name}: ${label} parser not yet implemented.`;
}
